import { useId, useState } from 'react';
import { CheckCircleFilled, MailOutlined, SendOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Input, InputPassword, Select } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { DongBatTat } from '../chung';
import { LOAI_TAI_KHOAN_HIEN } from '../../constants';
import { useProjects } from '../../hooks/useOpenCore';
import { useGuiThuTaiKhoan, useLuuTaiKhoanGui } from '../../hooks/useThongBao';
import { formTaiKhoan, GIOI_HAN_TAI_KHOAN, kiemTaiKhoan, LOAI_TAI_KHOAN, MA_HOA, thanTaiKhoan } from '../../nghiep-vu/thong-bao';
import type { FormTaiKhoan, LoiTaiKhoan } from '../../nghiep-vu/thong-bao';
import type { LoaiTaiKhoanGui, MaHoaSmtp, TaiKhoanGui } from '../../types';
import { ChonWorkflow } from './ChonWorkflow';

const ICON_LOAI: Record<LoaiTaiKhoanGui, typeof MailOutlined> = { gmail: MailOutlined, telegram: SendOutlined };

/**
 * Thêm / sửa tài khoản gửi (`SenderAccountFormDialog` của native): Gmail SMTP hoặc Telegram Bot. Gửi thử được trước
 * khi lưu, bằng đúng dữ liệu đang nhập. Khi sửa, để trống mật khẩu ứng dụng / Bot token là giữ cái đã lưu; không đổi
 * được loại.
 */
export function HopTaiKhoan({ goc, docProjectDuoc, onClose }: { goc: TaiKhoanGui | null; docProjectDuoc: boolean; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const luu = useLuuTaiKhoanGui();
  const thu = useGuiThuTaiKhoan();
  const projects = useProjects(docProjectDuoc);
  const dangSua = goc !== null;
  const [f, setF] = useState<FormTaiKhoan>(() => formTaiKhoan(goc));
  const [loi, setLoi] = useState<LoiTaiKhoan>({});
  const [guiToi, setGuiToi] = useState('');
  const [loiThu, setLoiThu] = useState<string | null>(null);
  const gmail = f.type === 'gmail';

  const doi = (moi: Partial<FormTaiKhoan>) => {
    setF((cu) => ({ ...cu, ...moi }));
    setLoi((cu) => {
      const sach = { ...cu };
      for (const k of Object.keys(moi)) delete sach[k as keyof LoiTaiKhoan];
      if ('enabled' in moi) delete sach.isDefault;
      return sach;
    });
    luu.reset();
    setLoiThu(null);
  };

  const gui = () => {
    const tim = kiemTaiKhoan(f, dangSua);
    setLoi(tim);
    if (Object.keys(tim).length > 0 || luu.isPending) return;
    luu.mutate(
      { id: goc?.id ?? null, than: thanTaiKhoan(f) },
      {
        onSuccess: () => {
          toast.success(t(dangSua ? 'rpaTaiKhoanGui.form.daLuu' : 'rpaTaiKhoanGui.form.daThem'));
          onClose();
        },
      },
    );
  };

  const guiThu = () => {
    const tim = kiemTaiKhoan(f, dangSua);
    delete tim.isDefault;
    setLoi(tim);
    if (Object.keys(tim).length > 0) return;
    const toi = guiToi.trim();
    if (!gmail && !toi) {
      setLoiThu(t('rpaTaiKhoanGui.form.canChatId'));
      return;
    }
    setLoiThu(null);
    thu.mutate(
      { ...(goc ? { id: goc.id } : {}), account: thanTaiKhoan(f), ...(toi ? { to: toi } : {}) },
      {
        onSuccess: () =>
          toast.success(
            gmail
              ? t('rpaTaiKhoanGui.form.daGuiMailThu', { toi: toi || f.email.trim() })
              : t('rpaTaiKhoanGui.form.daGuiTinThu'),
          ),
        onError: (e) => setLoiThu(dichLoi(e)),
      },
    );
  };

  const nhanLoi = (k: keyof LoiTaiKhoan, sai: string) => {
    const l = loi[k];
    if (!l) return undefined;
    if (l === 'batBuoc') return t('rpaTaiKhoanGui.form.batBuoc');
    if (l === 'macDinhPhaiBat') return t('rpaTaiKhoanGui.form.macDinhPhaiBat');
    return sai;
  };

  const loiChung = luu.error ? dichLoi(luu.error) : loiThu;

  return (
    <Dialog
      open
      onClose={onClose}
      width={640}
      destroyOnHidden
      title={goc ? t('rpaTaiKhoanGui.form.suaTieuDe', { ten: goc.name }) : t('rpaTaiKhoanGui.form.taoTieuDe')}
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="outline"
              size="md"
              icon={gmail ? <MailOutlined /> : <SendOutlined />}
              loading={thu.isPending}
              onClick={guiThu}
            >
              {thu.isPending
                ? t('rpaTaiKhoanGui.form.dangGuiThu')
                : t(gmail ? 'rpaTaiKhoanGui.form.guiMailThu' : 'rpaTaiKhoanGui.form.guiTinThu')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={luu.isPending}>
              {luu.isPending ? t('chung.dangLuu') : t(dangSua ? 'rpaTaiKhoanGui.form.luu' : 'rpaTaiKhoanGui.form.them')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <Field label={t('rpaTaiKhoanGui.form.ten')} required error={nhanLoi('name', '')}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              autoComplete="off"
              maxLength={GIOI_HAN_TAI_KHOAN.ten}
              placeholder={t('rpaTaiKhoanGui.form.tenMau')}
              value={f.name}
              onChange={(e) => doi({ name: e.target.value })}
            />
          )}
        </Field>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-content-primary">{t('rpaTaiKhoanGui.loai')}</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t('rpaTaiKhoanGui.loai')}>
            {LOAI_TAI_KHOAN.map((loai) => {
              const chon = f.type === loai;
              const Icon = ICON_LOAI[loai];
              const l = LOAI_TAI_KHOAN_HIEN[loai];
              return (
                <button
                  key={loai}
                  type="button"
                  role="radio"
                  aria-checked={chon}
                  disabled={dangSua && !chon}
                  onClick={() => setF((cu) => ({ ...formTaiKhoan(null, loai), name: cu.name }))}
                  className={cn(
                    'relative flex items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50',
                    chon ? 'border-primary-500 bg-primary-50' : 'border-default bg-surface hover:bg-subtle',
                  )}
                >
                  <Icon className="text-lg text-content-secondary" aria-hidden />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-content-primary">{t(l.khoa)}</span>
                    <span className="block text-xs text-content-muted">{t(l.moTa)}</span>
                  </span>
                  {chon && <CheckCircleFilled className="absolute right-3 top-3 text-primary-600" aria-hidden />}
                </button>
              );
            })}
          </div>
          {dangSua && <p className="mt-1.5 text-xs text-content-muted">{t('rpaTaiKhoanGui.loaiKhoa')}</p>}
        </fieldset>

        {gmail ? (
          <>
            <Field label={t('rpaTaiKhoanGui.form.email')} required error={nhanLoi('email', t('rpaTaiKhoanGui.form.emailSai'))}>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  autoComplete="off"
                  placeholder={t('rpaTaiKhoanGui.form.emailMau')}
                  value={f.email}
                  onChange={(e) => doi({ email: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('rpaTaiKhoanGui.form.appPassword')}
              required={!dangSua}
              hint={dangSua ? t('rpaTaiKhoanGui.form.giuBiMat') : t('rpaTaiKhoanGui.form.appPasswordGoiY')}
              error={nhanLoi('biMat', '')}
            >
              {(p) => (
                <InputPassword
                  {...p}
                  autoComplete="new-password"
                  maxLength={GIOI_HAN_TAI_KHOAN.matKhau}
                  value={f.biMat}
                  onChange={(e) => doi({ biMat: e.target.value })}
                />
              )}
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field
                className="sm:col-span-2"
                label={t('rpaTaiKhoanGui.form.smtpHost')}
                error={nhanLoi('smtpHost', t('rpaTaiKhoanGui.form.hostSai'))}
              >
                {(p) => (
                  <Input
                    {...p}
                    autoComplete="off"
                    maxLength={GIOI_HAN_TAI_KHOAN.host}
                    value={f.smtpHost}
                    onChange={(e) => doi({ smtpHost: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t('rpaTaiKhoanGui.form.smtpPort')} error={nhanLoi('smtpPort', t('rpaTaiKhoanGui.form.portSai'))}>
                {(p) => (
                  <Input {...p} inputMode="numeric" autoComplete="off" value={f.smtpPort} onChange={(e) => doi({ smtpPort: e.target.value })} />
                )}
              </Field>
            </div>
            <Field label={t('rpaTaiKhoanGui.form.maHoa')}>
              {(p) => (
                <Select<MaHoaSmtp>
                  id={p.id}
                  className="w-full"
                  value={f.encryption}
                  onChange={(encryption) => doi({ encryption })}
                  options={MA_HOA.map((m) => ({ value: m, label: t(`rpaTaiKhoanGui.maHoaKieu.${m}`) }))}
                />
              )}
            </Field>
          </>
        ) : (
          <Field
            label={t('rpaTaiKhoanGui.form.botToken')}
            required={!dangSua}
            hint={dangSua ? t('rpaTaiKhoanGui.form.giuBiMat') : t('rpaTaiKhoanGui.form.botTokenGoiY')}
            error={nhanLoi('biMat', t('rpaTaiKhoanGui.form.tokenSai'))}
          >
            {(p) => (
              <InputPassword
                {...p}
                autoComplete="off"
                maxLength={GIOI_HAN_TAI_KHOAN.token}
                value={f.biMat}
                onChange={(e) => doi({ biMat: e.target.value })}
              />
            )}
          </Field>
        )}

        <Field
          label={t(gmail ? 'rpaTaiKhoanGui.form.guiThuToiMail' : 'rpaTaiKhoanGui.form.guiThuToiTelegram')}
          hint={t(gmail ? 'rpaTaiKhoanGui.form.guiThuToiMailGoiY' : 'rpaTaiKhoanGui.form.guiThuToiTelegramGoiY')}
        >
          {(p) => (
            <Input
              {...p}
              autoComplete="off"
              placeholder={gmail ? f.email || t('rpaTaiKhoanGui.form.emailMau') : t('rpaThongBao.kenhMau.telegram')}
              value={guiToi}
              onChange={(e) => {
                setGuiToi(e.target.value);
                setLoiThu(null);
              }}
            />
          )}
        </Field>

        <Field label={t('rpaTaiKhoanGui.form.workflow')} hint={t('rpaTaiKhoanGui.form.workflowGoiY')}>
          {(p) => (
            <ChonWorkflow
              id={p.id}
              describedBy={p['aria-describedby']}
              projects={projects.data ?? []}
              loading={docProjectDuoc && projects.isPending}
              value={f.workflows}
              onChange={(workflows) => doi({ workflows })}
            />
          )}
        </Field>

        <div>
          <Checkbox checked={f.isDefault} onChange={(e) => doi({ isDefault: e.target.checked })}>
            {t('rpaTaiKhoanGui.form.macDinh')}
          </Checkbox>
          <p className={cn('mt-1 text-xs', loi.isDefault ? 'text-danger-700' : 'text-content-muted')} role={loi.isDefault ? 'alert' : undefined}>
            {loi.isDefault ? t('rpaTaiKhoanGui.form.macDinhPhaiBat') : t('rpaTaiKhoanGui.form.macDinhGoiY')}
          </p>
        </div>
        <DongBatTat
          nhan={t('rpaTaiKhoanGui.form.bat')}
          goiY={t('rpaTaiKhoanGui.form.batGoiY')}
          bat={f.enabled}
          onDoi={(enabled) => doi({ enabled })}
        />
      </form>
    </Dialog>
  );
}
